/**
 * CRM merge-field resolution — PURE, safe, and validating.
 *
 * Replaces the shallow string-splice merge with a governed registry:
 * every `{{token}}` a template may use is declared in `MERGE_FIELDS`
 * with a label, a resolver, and an explicit fallback. Resolution NEVER
 * emits "undefined" (the classic embarrassing mail-merge bug) — a field
 * that can't be resolved renders its fallback and is reported in
 * `missing` so the send path can block or warn before anything reaches
 * a customer. No customer data is ever fabricated: resolvers only read
 * what the caller supplies in the context.
 *
 * Also home to the accepted-quote → deal-amount bridge
 * (`acceptedConfigToDealAmount`): when a buyer's interactive selection
 * is approved, this single function defines the number written back to
 * the deal — always `computeTotals(config).total`, never a re-derived
 * figure that could drift.
 */

import { computeTotals, type QuoteConfig, type QuoteTotals } from './quoteBlock';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

/**
 * Everything a template may draw from. Structurally compatible with the
 * db records (`Account.name`, `Account.ownerName`, Person contacts) but
 * deliberately loose so callers can pass partial snapshots.
 */
export interface MergeContext {
  account?: { name?: string; ownerName?: string } | null;
  /** Primary contact first — {{contact.*}} resolves from contacts[0]. */
  contacts?: Array<{ name?: string; email?: string; title?: string }> | null;
  deal?: { name?: string; amount?: number; stage?: string } | null;
  owner?: { name?: string; email?: string } | null;
  quoteTotals?: QuoteTotals | null;
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

/** Resolver metadata for one merge token. */
export interface MergeFieldDef {
  /** The token as written in templates, e.g. "account.name". */
  token: string;
  /** Human label for pickers / validation messages. */
  label: string;
  /** What renders when the field can't be resolved — never "undefined". */
  fallback: string;
  /** True when a send should be blocked if this field is unresolved. */
  required?: boolean;
  /** Pull the value from the context; undefined = missing. */
  resolve: (ctx: MergeContext) => string | undefined;
}

const usd = (n: number): string =>
  '$' +
  n.toLocaleString('en-US', {
    minimumFractionDigits: Number.isInteger(Math.round(n * 100) / 100) ? 0 : 2,
    maximumFractionDigits: 2,
  });

const str = (v: string | undefined | null): string | undefined =>
  typeof v === 'string' && v.trim() !== '' ? v : undefined;

const money = (v: number | undefined | null): string | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? usd(v) : undefined;

/** Every merge token templates may use, with resolver metadata. */
export const MERGE_FIELDS: MergeFieldDef[] = [
  {
    token: 'account.name',
    label: 'Account name',
    fallback: 'your company',
    required: true,
    resolve: (ctx) => str(ctx.account?.name),
  },
  {
    token: 'account.owner',
    label: 'Account owner',
    fallback: 'your account team',
    resolve: (ctx) => str(ctx.account?.ownerName),
  },
  {
    token: 'contact.name',
    label: 'Primary contact name',
    fallback: 'there',
    resolve: (ctx) => str(ctx.contacts?.[0]?.name),
  },
  {
    token: 'contact.email',
    label: 'Primary contact email',
    fallback: '—',
    resolve: (ctx) => str(ctx.contacts?.[0]?.email),
  },
  {
    token: 'contact.title',
    label: 'Primary contact title',
    fallback: '—',
    resolve: (ctx) => str(ctx.contacts?.[0]?.title),
  },
  {
    token: 'deal.name',
    label: 'Deal name',
    fallback: 'this engagement',
    resolve: (ctx) => str(ctx.deal?.name),
  },
  {
    token: 'deal.amount',
    label: 'Deal amount',
    fallback: 'the quoted amount',
    required: true,
    resolve: (ctx) => money(ctx.deal?.amount),
  },
  {
    token: 'deal.stage',
    label: 'Deal stage',
    fallback: '—',
    resolve: (ctx) => str(ctx.deal?.stage),
  },
  {
    token: 'owner.name',
    label: 'Sender name',
    fallback: 'The team',
    resolve: (ctx) => str(ctx.owner?.name),
  },
  {
    token: 'quote.total',
    label: 'Quote total',
    fallback: 'the quoted total',
    required: true,
    resolve: (ctx) => money(ctx.quoteTotals?.total),
  },
  {
    token: 'quote.recurring',
    label: 'Quote recurring (annualized)',
    fallback: '—',
    resolve: (ctx) => money(ctx.quoteTotals?.recurringTotal),
  },
];

const REGISTRY = new Map(MERGE_FIELDS.map((f) => [f.token, f]));

// ---------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------

const TOKEN_RE = /\{\{\s*([\w.]+)\s*\}\}/g;

export interface MergeResult {
  /** Fully rendered text — every token replaced, never "[undefined]". */
  text: string;
  /** Tokens that fell back (unresolved or unknown), for validation. */
  missing: string[];
  /** token → rendered value, for preview/inspection UIs. */
  resolved: Record<string, string>;
}

/**
 * Render a template against a context. Every `{{token}}` is replaced:
 * known tokens by their resolved value or declared fallback, unknown
 * tokens by a neutral em-dash. Anything that fell back lands in
 * `missing` so callers can gate the send.
 */
export function resolveMerge(template: string, ctx: MergeContext): MergeResult {
  const missing: string[] = [];
  const resolved: Record<string, string> = {};

  const text = template.replace(TOKEN_RE, (_whole, token: string) => {
    const def = REGISTRY.get(token);
    if (!def) {
      if (!missing.includes(token)) missing.push(token);
      return '—';
    }
    const value = def.resolve(ctx);
    if (value === undefined) {
      if (!missing.includes(token)) missing.push(token);
      resolved[token] = def.fallback;
      return def.fallback;
    }
    resolved[token] = value;
    return value;
  });

  return { text, missing, resolved };
}

/**
 * Pre-send validation: ok only when every token in the template resolves
 * from the context (no fallbacks needed, no unknown tokens).
 */
export function validateMerge(
  template: string,
  ctx: MergeContext,
): { ok: boolean; missing: string[] } {
  const { missing } = resolveMerge(template, ctx);
  return { ok: missing.length === 0, missing };
}

// ---------------------------------------------------------------------------
// Accepted config → deal write value
// ---------------------------------------------------------------------------

/**
 * The single source of truth for what an accepted interactive quote is
 * worth to the deal: the recomputed total of the final configuration.
 * The approval flow shows `buildPricingDiff(base, accepted)` and, on
 * confirm, writes THIS number — nothing else — to the deal amount.
 */
export function acceptedConfigToDealAmount(cfg: QuoteConfig): number {
  return computeTotals(cfg).total;
}
